import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, ExternalLink, Link2, ListTree, Loader2, PackagePlus, RefreshCw, ShoppingCart, X } from 'lucide-react';
import { apiService } from '../../../services/api';
import CadThumb from '../CadThumb';
import { Card, Empty, GhostButton } from '../ui';
import ItemsTab from './ItemsTab';

// PLM du projet, rapproché de sa CAO Fusion et de ses achats (demande de Rémy, 2026-10-12 : « lier le PLM à Muse et CAO
// pour qu'on fasse plein de liens facilement », « lier une version de Fusion dans le PLM »). En haut, les conceptions
// Fusion (vignette, masse, ce qui a changé, version du PLM liée) ; puis chaque pièce des nomenclatures avec sa pièce du
// PLM et ses achats, liés ou proposés (un clic pour valider, écarter, ou créer la pièce) ; en bas, le stock du projet.

const qty = (n) => `×${n}`;
const day = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '');
const amount = (value) => (value == null ? '' : `${Number(value).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} €`);

const Chip = ({ tone = 'slate', children, onRemove = null, title }) => {
  const tones = {
    slate: 'border-slate-200 bg-slate-50 text-slate-600', indigo: 'border-indigo-200 bg-indigo-50 text-indigo-700',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700', amber: 'border-amber-200 bg-amber-50 text-amber-700',
  };
  return (
    <span title={title} className={`inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${tones[tone]}`}>
      <span className="truncate">{children}</span>
      {onRemove && <button type="button" onClick={onRemove} aria-label="Retirer le lien" className="shrink-0 opacity-60 hover:opacity-100"><X size={11} /></button>}
    </span>
  );
};

const Action = ({ onClick, busy, children, tone = 'indigo', title }) => (
  <button type="button" onClick={onClick} disabled={busy} title={title}
    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold disabled:opacity-50 ${tone === 'indigo'
      ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'}`}>
    {busy && <Loader2 size={11} className="animate-spin" />}{children}
  </button>
);

const ItemPicker = ({ items, onPick, label = 'Autre pièce du PLM…' }) => (
  <select value="" onChange={(e) => e.target.value && onPick(Number(e.target.value))}
    className="max-w-[11rem] rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] text-slate-500">
    <option value="">{label}</option>
    {items.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
  </select>
);

const DesignCard = ({ projectId, design, items, act, busy }) => {
  const key = `design-${design.item_id}`;
  const base = `/projects/${projectId}/plm`;
  const link = (body) => act(key, `${base}/designs`, { cad_item_id: design.item_id, ...body });
  return (
    <div className="flex gap-3 rounded-2xl border border-slate-200 bg-white p-3">
      <CadThumb projectId={projectId} itemId={design.item_id} version={design.version} available={design.thumbnail} />
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-900">{design.name}</p>
            <p className="truncate text-xs text-slate-500">
              v{design.version} · {design.parts.reduce((n, p) => n + p.qty, 0)} pièces{design.mass ? ` · ${design.mass.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${design.mass_unit || ''}` : ''} · {design.source}
            </p>
          </div>
          {design.url && <a href={design.url} target="_blank" rel="noopener noreferrer" title="Ouvrir dans Fusion" className="shrink-0 text-slate-400 hover:text-indigo-600"><ExternalLink size={14} /></a>}
        </div>
        {design.changes?.length > 0 && (
          <div className="flex flex-wrap gap-1">{design.changes.slice(0, 5).map((c) => <Chip key={c} tone="amber">{c}</Chip>)}</div>
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          {design.plm ? (
            <>
              <Chip tone="indigo" onRemove={() => act(key, `/plm/links/${design.plm.link_id}`, null, 'delete')}>
                PLM : {design.plm.name}{design.plm.version != null ? ` · V${design.plm.version}` : ''}
              </Chip>
              <Action busy={busy === `${key}-bom`} title="Ajoute chaque pièce de la CAO (créée dans le PLM au besoin) à la nomenclature de cette version du PLM"
                onClick={() => act(`${key}-bom`, `${base}/import-bom`, { cad_item_id: design.item_id })}>
                <ListTree size={11} /> Importer la nomenclature
              </Action>
            </>
          ) : (
            <>
              {(design.plm_suggestions || []).map((s) => (
                <Action key={s.item_id} busy={busy === key} onClick={() => link({ inventory_item_id: s.item_id })}>
                  <Link2 size={11} /> Lier à « {s.name} »
                </Action>
              ))}
              <Action busy={busy === key} tone={design.plm_suggestions?.length ? 'ghost' : 'indigo'} onClick={() => link({})}>
                <PackagePlus size={11} /> Créer dans le PLM
              </Action>
              {items.length > 0 && <ItemPicker items={items} onPick={(id) => link({ inventory_item_id: id })} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const PartRow = ({ projectId, part, items, act, busy }) => {
  const key = `part-${part.key}`;
  const base = `/projects/${projectId}/plm`;
  const linkPart = (body) => act(key, `${base}/parts`, { part_key: part.key, ...body });
  const decline = (candidate) => act(key, `${base}/decline`, { part_key: part.key, candidate });
  const linkPurchase = async (line) => {
    if (part.plm) return act(key, '/plm/purchases', { inventory_item_id: part.plm.item_id, expense_line_id: line.id });
    // Pas encore de pièce PLM : on la crée (depuis la CAO), puis on relie l'achat.
    const view = await act(key, `${base}/parts`, { part_key: part.key });
    const created = view?.parts?.find((p) => p.key === part.key)?.plm;
    if (created) await act(key, '/plm/purchases', { inventory_item_id: created.item_id, expense_line_id: line.id });
    return null;
  };
  return (
    <tr className="align-top">
      <td className="py-2 pr-3">
        <p className="text-sm font-medium text-slate-800">{part.name}</p>
        <p className="text-xs text-slate-400">
          {[part.part_number !== part.name && part.part_number, part.material].filter(Boolean).join(' · ')}
          {part.used_in?.length ? ` · dans ${[...new Set(part.used_in)].slice(0, 2).join(', ')}` : ''}
        </p>
      </td>
      <td className="py-2 pr-3 text-sm tabular-nums text-slate-600">{qty(part.qty)}</td>
      <td className="py-2 pr-3">
        <div className="flex flex-wrap items-center gap-1">
          {part.plm ? (
            <Chip tone="indigo" onRemove={() => act(key, `/plm/links/${part.plm.link_id}`, null, 'delete')}>{part.plm.name}</Chip>
          ) : (
            <>
              {part.plm_suggestions.map((s) => (
                <span key={s.item_id} className="inline-flex items-center gap-0.5">
                  <Action busy={busy === key} onClick={() => linkPart({ inventory_item_id: s.item_id })}><Link2 size={11} /> {s.name}</Action>
                  <button type="button" onClick={() => decline(`plm:${s.item_id}`)} title="Ce n'est pas cette pièce" className="text-slate-300 hover:text-rose-500"><X size={12} /></button>
                </span>
              ))}
              <Action busy={busy === key} tone="ghost" onClick={() => linkPart({})}><PackagePlus size={11} /> Créer</Action>
              {items.length > 0 && <ItemPicker items={items} onPick={(id) => linkPart({ inventory_item_id: id })} label="Choisir…" />}
            </>
          )}
        </div>
      </td>
      <td className="py-2">
        <div className="flex flex-wrap items-center gap-1">
          {part.purchases.map((p) => (
            <Chip key={p.id} tone="emerald" title={p.designation} onRemove={() => act(key, `/plm/links/${p.link_id}?purchase=true`, null, 'delete')}>
              {p.quantity ? `${p.quantity}× ` : ''}{p.vendor} {day(p.date)}
            </Chip>
          ))}
          {!part.purchases.length && part.purchase_suggestions.map((s) => (
            <span key={s.id} className="inline-flex items-center gap-0.5">
              <Action busy={busy === key} tone="ghost" title={`${s.designation} · ${amount(s.total)}`} onClick={() => linkPurchase(s)}>
                <ShoppingCart size={11} /> {s.vendor} {day(s.date)} ?
              </Action>
              <button type="button" onClick={() => decline(`achat:${s.id}`)} title="Ce n'est pas cet achat" className="text-slate-300 hover:text-rose-500"><X size={12} /></button>
            </span>
          ))}
          {!part.purchases.length && !part.purchase_suggestions.length && <span className="text-[11px] text-slate-300">—</span>}
        </div>
      </td>
    </tr>
  );
};

const FILTERS = [['toutes', 'Toutes'], ['relier', 'À relier'], ['liees', 'Liées au PLM']];

const PlmTab = (props) => {
  const projectId = props.projectId || props.project?.id;
  const [view, setView] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('toutes');

  const load = useCallback(() => apiService.get(`/projects/${projectId}/plm`).then(setView).catch((e) => setError(e.message)),
    [projectId]);
  const loadItems = useCallback(() => apiService.get('/inventory/items').then((r) => setItems(Array.isArray(r) ? r : r?.items || []))
    .catch(() => {}), []);
  useEffect(() => { if (projectId) { load(); loadItems(); } }, [projectId, load, loadItems]);

  // Chaque action renvoie la vue du projet à jour (ou on la recharge) ; le catalogue du PLM peut avoir grandi.
  const act = async (key, url, body, method = 'post') => {
    setBusy(key);
    setError(null);
    try {
      const result = method === 'delete' ? await apiService.delete(url) : await apiService.post(url, body);
      // Seule la vue du projet porte plm_items ; la fiche d'une pièce du PLM (lien d'achat) a d'autres « parts ».
      if (result && 'plm_items' in result) setView(result); else await load();
      loadItems();
      return result;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setBusy(null);
    }
  };

  const parts = useMemo(() => (view?.parts || []).filter((p) => (filter === 'relier' ? !p.plm : filter === 'liees' ? p.plm : true)),
    [view, filter]);
  const linked = (view?.parts || []).filter((p) => p.plm).length;
  const bought = (view?.parts || []).filter((p) => (p.purchases || []).length).length;

  return (
    <div className="space-y-5">
      {error && <p className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
      <Card title="Conceptions Fusion" icon={Box}
        action={<GhostButton onClick={load}><RefreshCw size={14} /> Actualiser</GhostButton>}>
        {!view && <p className="flex items-center gap-2 text-sm text-slate-400"><Loader2 size={14} className="animate-spin" /> Chargement…</p>}
        {view && view.designs.length === 0 && (
          <Empty>Aucune nomenclature Fusion pour ce projet : relie son dossier Fusion (bouton « CAO » en haut de la fiche) ; elle arrive au relevé suivant.</Empty>
        )}
        <div className="grid gap-3 lg:grid-cols-2">
          {(view?.designs || []).map((d) => <DesignCard key={d.item_id} projectId={projectId} design={d} items={items} act={act} busy={busy} />)}
        </div>
      </Card>

      {view?.parts?.length > 0 && (
        <Card title="Pièces des nomenclatures" icon={ListTree}
          action={(
            <div className="flex gap-1">
              {FILTERS.map(([k, label]) => (
                <button key={k} type="button" onClick={() => setFilter(k)}
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold ${filter === k ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>{label}</button>
              ))}
            </div>
          )}>
          <p className="mb-2 text-xs text-slate-500">
            {view.parts.length} pièces · {linked} liées au PLM · {bought} avec un achat. Deepflow propose les liens évidents (même nom, même référence) : un clic pour valider, la croix pour écarter.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left">
              <thead>
                <tr className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                  <th className="pb-1 pr-3">Pièce (CAO)</th><th className="pb-1 pr-3">Qté</th><th className="pb-1 pr-3">PLM</th><th className="pb-1">Achats</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {parts.map((p) => <PartRow key={p.key} projectId={projectId} part={p} items={items} act={act} busy={busy} />)}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {view?.purchased?.length > 0 && (
        <Card title="Pièces du PLM achetées pour le projet" icon={ShoppingCart}>
          <p className="mb-2 text-xs text-slate-500">
            Reliées à une facture du projet : ajoutées au PLM depuis un achat (proposition de Nexus) ou reliées à la main.
          </p>
          <ul className="divide-y divide-slate-100">
            {view.purchased.map((item) => (
              <li key={item.item_id} className="flex flex-wrap items-center gap-2 py-2">
                <span className="text-sm font-medium text-slate-800">{item.name}</span>
                {item.in_cad && <Chip tone="indigo">dans la CAO</Chip>}
                {item.purchases.map((p) => (
                  <Chip key={p.id} tone="emerald" title={p.designation}
                    onRemove={() => act(`achat:${item.item_id}`, `/plm/links/${p.link_id}?purchase=true`, null, 'delete')}>
                    {p.quantity ? `${p.quantity}× ` : ''}{p.vendor} {day(p.date)}
                  </Chip>
                ))}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Stock du projet</h3>
        <ItemsTab {...props} />
      </section>
    </div>
  );
};

export default PlmTab;
